import { captureException } from '@sentry/react';
import type { AuthUserQuery } from '@graphql/reactQuery';
import { LogoutDocument, ReactQueryEffects } from '@graphql/reactQuery';
import type { NonNullableObject } from '@utils/types';

const AuthEffectsList = {
  loginStart: new Set<() => void>(),
  loginStop: new Set<() => void>(),

  loginSuccessStart: new Set<(data: { authToken: string }) => void>(),
  loginSuccessEnd: new Set<(data: NonNullableObject<AuthUserQuery>) => void>(),
  loginFail: new Set<() => void>(),

  logoutStart: new Set<() => void>(),
  logoutMutationSuccess: new Set<() => void>(),
  logoutComplete: new Set<() => void>(),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} as const satisfies Record<string, Set<(...args: any[]) => unknown>>;

type EffectName = keyof typeof AuthEffectsList;

type IterableValue<T extends Iterable<unknown>> = T extends Iterable<infer Value> ? Value : never;

type EffectParameters<Effect extends EffectName> = Parameters<
  IterableValue<(typeof AuthEffectsList)[Effect]>
>;

type EffectCallback<Effect extends EffectName> = (
  callback: (...params: EffectParameters<Effect>) => void,
) => () => void;

function AuthEffectSubscribe<Effect extends EffectName>(effect: Effect): EffectCallback<Effect> {
  const callbacks = AuthEffectsList[effect];

  return function subscribe(cb) {
    callbacks.add(cb);

    return function cleanup() {
      callbacks.delete(cb);
    };
  };
}

type AuthEffects = {
  [Effect in EffectName]: EffectCallback<Effect>;
};

function isEffectName(effect: string): effect is EffectName {
  return effect in AuthEffectsList;
}

export const AuthEffects = Object.keys(AuthEffectsList).reduce((acc, effect) => {
  if (isEffectName(effect)) {
    acc[effect] = AuthEffectSubscribe(effect);
  }
  return acc;
}, {} as AuthEffects);

export function invokeAuthEffect<Effect extends EffectName>(
  authEffect: Effect,
  ...payload: EffectParameters<Effect>
) {
  for (const cb of AuthEffectsList[authEffect]) {
    try {
      Promise.resolve(
        cb(
          // @ts-expect-error Typescript struggles with this generic value
          ...payload,
        ),
      ).catch(err =>
        captureException(err, {
          level: 'warning',
          extra: {
            authEffect,
            payload,
          },
        }),
      );
    } catch (err) {
      captureException(err, {
        level: 'warning',
        extra: {
          authEffect,
          payload,
        },
      });
    }
  }
}

ReactQueryEffects.onCompleted(LogoutDocument, () => invokeAuthEffect('logoutMutationSuccess'));
